import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { useTranslation } from "react-i18next";
import AddModal from "../../../Components/Common/Modal/AddModal";
import AddLocalizationForm from "../../../Components/Common/AddLocalizationForm/AddLocalizationForm";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLocalization,
  fetchScreens,
  createLocalization,
  updateLocalization,
  fetchLocalizationTableColumnNames,
  deleteLocalization,
  updateUserPreference,
  fetchScreenTableColumnNames,
} from "../../../slices/localization/thunk";
import { getAuthUser } from "../../../helpers/utils";
import { Link } from "react-router-dom";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import { toast } from "react-toastify";
import ManageScreen from "./ManageScreen";
import { fetchTranslation } from "../../../slices/thunks";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import usePermissions from "../../../Components/Hooks/usePermissions";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import { min } from "lodash";

// Updated labels configuration
const labels = [
  {
    id: 1,
    labelName: "LabelKey",
    placeholder: "LabelKey",
    fieldName: "text-box",
    name: "LabelKey",
    value: "LabelKey",
    required: true,
    minLength: 3,
    minLengthErrorMessage: "LabelKeyMin",
    maxLength: 150,
    maxLengthErrorMessage: "LabelKeyMax",
    errorMessage: "Label Key is required",
    disabled: (mode) => mode === 'isEdit', 
  },
  {
    id: 2,
    labelName: "Screen",
    fieldName: "screen-selection-dropdown",
    name: "ScreenID",
    value: "ScreenID",
    required: true,
    errorMessage: "Screen is required",
  },
  {
    id: 3,
    labelName: "ValueArabic",
    placeholder: "EnterValueArabic",
    fieldName: "text-box-translate",
    name: "ValueAr",
    value: "ValueAr",
    required: true,
    errorMessage: "Value Arabic is required",
    translateTo: "ValueEn",
  },
  {
    id: 4,
    labelName: "ValueEnglish",
    placeholder: "EnterValueEnglish",
    fieldName: "text-box",
    name: "ValueEn",
    value: "ValueEn",
    required: true,
    errorMessage: "Value English is required", 
  },
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
  // permission: {
  //   icon: "ri-lock-unlock-line",
  //   tooltip: "Permission",
  //   // isVisible: (item) => item.Id != 2,
  // }
};

function Localization() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const language = localStorage.getItem("I18N_LANGUAGE");
  const {user} =  getAuthUser();
  const isRTL = i18n.dir() === "rtl";

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [screenTab, setScreenTab] = useState(false);
  const [lastTranslatedField, setLastTranslatedField] = useState(null); // To track which field is being translated
  
  const { localization, tableColumnNames, loading, Translations, translationLoading } = useSelector(
    (state) => ({
      localization: state.Localization.localization,
      tableColumnNames: state.Localization.tableColumnNames,
      loading: state.Localization.loading,
      Translations: state.Module.translations, 
      translationLoading: state.Module.translationLoading, // Get translation loading state
    })
  );


  // Compute permission
  const permission = (() => {
    // moduleId: 1 (Administration), pageId: 2 (localization)
    return usePermissions(1, 1);
  })();



  const handleClassesClicks = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (localization, value) => {
    setSelectedRow(localization);
    setIsAddOrEdit(value);
    setShow(true);
  };



 const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
      toast.error(t("EnterTextToTranslate"));
      return;
    }
    setLastTranslatedField(sourceFieldName); // Set the field being translated

    dispatch(fetchTranslation({ LangText: textToTranslate }))
      .unwrap() 
      .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
      })
      .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
      })
      .finally(() => {
        setLastTranslatedField(null); // Reset after completion or error
      });
  };
  
  const onSubmitHandler = (values, { resetForm }) => {

    const localizationObj = {
      LabelKey: values.LabelKey.trim(),
      ValueEn: values.ValueEn.trim(),
      ValueAr: values.ValueAr.trim(),
      ScreenID: values.ScreenID,
    };

    if (isAddOrEdit === "isAdd") {
      dispatch(createLocalization({localizationObj})); 
    } else {
      localizationObj.ID = selectedRow.ID;
      dispatch(updateLocalization({localizationObj})); 
    }
    resetForm(); // Reset form after submission
    setShow(false);
  };

  const confirmDelete = () => {
    dispatch(deleteLocalization({ Ids: selectedIds, UserID: user.UserID }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchLocalizationTableColumnNames({
          module: "LOCALIZATION"
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  useEffect(() => {
    dispatch(fetchScreens());
    dispatch(
      fetchScreenTableColumnNames({
        module: "SCREENS",
      })
    );

    dispatch(
      fetchLocalizationTableColumnNames({
        module: "LOCALIZATION"
      })
    ).then(() => {
      dispatch(
        fetchLocalization({
          language
        })
      );    });
  }, [dispatch]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-3">
            <Col>
              <BreadCrumb title={t("Localization")} />
            </Col>
          </Row>

          {screenTab === false && (
            <div className="table-responsive">
              <Card>
                <CardHeader>
                  <Row className="g-4">
                        <Col className="col-sm-auto">
                          <div>
                            <Button
                              className="btn btn-soft-danger ms-2 me-2"
                              onClick={() => setDeleteModal(true)}
                              disabled={selectedIds.length === 0}
                            >
                              <i className="ri-delete-bin-2-line"></i>
                            </Button>
                            {permission?.CanAdd && (
                              <Button
                                color="secondary"
                                className="add-btn me-1"
                                onClick={() => handleClassesClicks("isAdd")}
                                id="create-btn"
                                disabled={loading}
                              >
                                <i className="ri-add-line align-bottom me-1 ms-1"></i>
                                {t("AddNew")}
                              </Button>
                            )}
                            <Button
                              color="secondary"
                              className="add-btn me-1"
                              onClick={() => setScreenTab(true)}
                              id="create-btn"
                              disabled={loading}
                            >
                              <i className="ri-add-line align-bottom me-1 ms-1"></i>
                              {t("Add Screens")}
                            </Button>
                          </div>
                        </Col>
                        <Col className="col-sm">
                          <div className="d-flex justify-content-sm-end">
                            <Button
                              className="btn btn-soft"
                              onClick={() => setModalOpen(true)}
                              disabled={loading}
                            >
                              <i className="ri-filter-3-line"></i>
                            </Button>
                            <div className="search-box ms-2 me-2">
                              <Input
                                type="text"
                                className="form-control search"
                                placeholder={t("Search")}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                              <i className="ri-search-line search-icon"></i>
                            </div>
                          </div>
                        </Col>
                  </Row>   
                </CardHeader>
                <CardBody>
           
                    {loading ? (
                      <div className="d-flex justify-content-center align-items-center">
                        <Spinner
                          style={{ height: "3rem", width: "3rem" }}
                          className="me-2"
                        >
                          Loading...
                        </Spinner>
                      </div>
                    ) : (
                      <div className="listjs-table" id="customerList">
                      <CommonDataTable
                        data={localization}
                        tableColumnNames={tableColumnNames}
                        searchTerm={searchTerm}
                        onEditClickHandler={onEditClickHandler}
                        setDeleteModal={setDeleteModal}
                        setSelectedIds={setSelectedIds}
                        actionConfig={actionConfig}
                        permission={permission}
                      />
                      </div>
                    )}
                </CardBody>
              </Card>
            </div>
          )}

          <ManageScreen
            shows={screenTab}
            setScreenTab={setScreenTab}
            permission={permission}
          />
        </Container>
      </div>

       {/* Modal  */}
       <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={AddLocalizationForm}
        FormBodyProps={{ onTranslateClickHandler, translationLoading, lastTranslatedField }} 
        title={isAddOrEdit === "isAdd" ? t("AddNewLocalization") : t("EditLocalization")}
        size="lg"
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />
    </React.Fragment>
  );
}

export default Localization;
